/**
 * CAMPOS DE FORMULÁRIO DO TUTORIAL
 *
 * Este arquivo contém somente configurações de campos.
 * Ele NÃO monta fieldsets, tabs, accordions ou grids externos.
 * Essa organização fica em forms-layout.js.
 *
 * A ideia é simples: se um desenvolvedor quiser lembrar como configurar um
 * campo, ele abre este arquivo e procura pelo nome do componente.
 */

/**
 * COMMON
 *
 * Campos de uso comum. Mantidos juntos porque este era o agrupamento original
 * do tutorial e continua sendo uma boa referência para propriedades básicas.
 */
export const common = {
  legend: 'Common',
  cols: 4,
  rows: 2,
  row: 0,
  col: 1,
  rowspan: 2,
  isGrid: true,
  content: [
    {
      element: 'text',
      id: 'id1',
      full: true,
      label: 'Nome',
      row: 0,
      col: 0,
      colspan: 2,
      required: true,
      disabled: true,
      value: 'Wellington'
    },
    {
      element: 'text',
      id: 'id2',
      full: true,
      label: 'Sobrenome',
      row: 0,
      col: 2,
      colspan: 2,
      required: true,
      readOnly: true,
      value: 'Pereira'
    },
    {
      element: 'email',
      row: 1,
      col: 0,
      required: true,
      colspan: 2,
      full: true,
      value: 'wrppereira@gmail.com',
      tooltip: '<p>Aqui você digita seu email<br>A menos que você não queira</p>'
    },
    {
      element: 'date',
      label: 'Data de Nascimento',
      row: 1,
      col: 2,
      min: '2022-10-15',
      max: '2028-04-06',
      value: '2024-12-24'
    },
    {
      element: 'time',
      label: 'Hora de Nascimento',
      row: 1,
      col: 3,
      value: '10:30'
    },
    {
      element: 'period',
      row: 2,
      col: 0,
      full: true,
      colspan: 2,
      min: '2025-01-03',
      max: '2025-01-28',
      value: ['2025-01-10', '2025-01-18'],
      label: ['Ida', 'Volta']
    },
    {
      element: 'datetime',
      row: 2,
      col: 2,
      full: true,
      colspan: 2,
      value: '2025-04-06 10:30',
      label: 'Início'
    },
    {
      element: 'password',
      label: 'Cadastre a senha',
      row: 3,
      col: 0,
      colspan: 2,
      strength: true
    },
    {
      element: 'password',
      row: 3,
      col: 2,
      colspan: 2
    },
    {
      element: 'search',
      row: 4,
      col: 0,
      colspan: 2,
      full: true,
      placeholder: 'Faça sua pesquisa aqui'
    },
    {
      element: 'url',
      row: 4,
      col: 2,
      colspan: 2,
      full: true,
      placeholder: 'Digite seu site aqui'
    }
  ]
};

/**
 * MASK
 *
 * Campos com máscara ou validação especializada.
 */
export const mask = {
  legend: 'Mask',
  color: '#99FF00',
  row: 1,
  col: 0,
  isGrid: true,
  content: [
    {
      element: 'rg',
      row: 0,
      col: 0,
      value: '275591438',
      tooltip: 'Digite seu <b style="color:var(--highlighted-text-color)">RG</b> aqui'
    },
    {
      element: 'cpf',
      row: 0,
      col: 1,
      value: '24931897878'
    },
    {
      element: 'cnpj',
      row: 1,
      col: 0,
      value: '09127157000157',
      helper: 'Digite seu <b style="color:var(--highlighted-text-color)">CNPJ</b> aqui e ele será validado automaticamente.'
    },
    {
      element: 'cep',
      value: '05717200',
      suffix: {
        icon: { type: 'solid', name: 'location-dot' },
        click: () => {
          document.getElementById('dialog').dialog('open');
        }
      },
      row: 1,
      col: 1
    },
    {
      element: 'creditCard',
      value: '5550537240031058',
      row: 2,
      col: 0
    },
    {
      element: 'phone',
      nationalMode: false,
      row: 2,
      col: 1,
      value: '+12015550123'
    }
  ]
};

/**
 * SLIDER E NÚMEROS
 *
 * Os handlers mostram explicitamente como sincronizar um slider com um campo
 * numérico. A repetição é intencional para o exemplo ser copiável isoladamente.
 */
export const slidersAndNumbers = {
  legend: 'Slider e Números',
  isGrid: true,
  rowspan: 4,
  content: [
    {
      element: 'slider',
      id: 'sliderV',
      label: 'Slider',
      display: true,
      style: { height: '280px' },
      value: 37,
      row: 0,
      col: 0,
      rowspan: 3,
      min: 0,
      max: 100,
      orient: 'vertical',
      input: (e) => {
        const val = e.target.value;
        document.getElementById('counterV').value = val;
      }
    },
    {
      element: 'slider',
      id: 'sliderH',
      label: 'Slider',
      display: true,
      style: { width: '280px' },
      value: 37,
      row: 0,
      col: 1,
      colspan: 2,
      min: 0,
      max: 100,
      orient: 'horizontal',
      input: (e) => {
        const val = e.target.value;
        document.getElementById('counterH').value = val;
      }
    },
    {
      element: 'knob',
      label: 'Pan',
      row: 1,
      col: 1,
      value: 0,
      display: true,
      scale: [-64, '', -32, '', 0, '', 32, '', 64]
    },
    {
      element: 'knob',
      label: 'Volume',
      row: 1,
      col: 2,
      value: 80,
      display: true
    },
    {
      element: 'float',
      label: 'Número',
      row: 2,
      col: 1,
      value: 3487.54
    },
    {
      element: 'currency',
      label: 'Currency',
      row: 2,
      col: 2,
      value: 125789.64
    },
    {
      element: 'integer',
      label: 'Inteiro',
      id: 'counterV',
      row: 3,
      col: 1,
      value: () => document.getElementById('sliderV')?.value ?? 48,
      blur: (e) => {
        const val = e.target.value;
        document.getElementById('sliderV').value = val;
      }
    },
    {
      element: 'integer',
      label: 'Inteiro',
      id: 'counterH',
      row: 3,
      col: 2,
      value: () => document.getElementById('sliderH')?.value ?? 37,
      blur: (e) => {
        const val = e.target.value;
        document.getElementById('sliderH').value = val;
      }
    }
  ]
};

/**
 * Exemplo real do componente Geography do EP.
 */
export const geography = {
  legend: 'Geografia',
  isGrid: true,
  cols: 3,
  content: [
    {
      element: 'geography',
      id: 'tutorial-geography',
      row: 0,
      col: 0,
      colspan: 3,
      countries: true,
      states: true,
      cities: true
    }
  ]
};

/**
 * SPINNERS
 */
export const spinners = {
  legend: 'Spinners',
  isGrid: true,
  cols: 4,
  content: [
    { element: 'spinner', mode: 'moreless', value: 12, style: { width: '4rem' } },
    { element: 'spinner', mode: 'leftright', value: 9.6, step: 0.2, style: { width: '4rem' } },
    { element: 'spinner', mode: 'none', value: 35.4, style: { width: '4rem' } },
    { element: 'spinner', value: 17, style: { width: '4rem' }, tooltip: '<p>Isso aqui é um Spinner<br>Acreidite quem quiser</p>' }
  ]
};

/**
 * CHECKBOX E RADIO
 */
export const checkboxRadio = {
  legend: 'Checkbox & Radio',
  isGrid: true,
  content: [
    {
      element: 'checkbox',
      label: 'PCD',
      text: '',
      colspan: 1,
      row: 0,
      col: 0,
      checked: false
    },
    {
      element: 'checkbox',
      appearance: 'switch',
      label: null,
      text: 'Modo escuro',
      row: 0,
      col: 1,
      disabled: false,
      checked: true,
      value: 'dark',
      change: (e) => {
        document.body.classList.remove('dark', 'light');
        if (e.target.checked === true) {
          document.body.classList.add('dark');
        } else {
          document.body.classList.add('light');
        }
      }
    },
    { element: 'radio', label: 'Radio', text: 'Radio R1', row: 1, col: 0, name: 'radio', value: 1, checked: true },
    { element: 'radio', label: 'Radio', text: 'Radio R2', row: 1, col: 1, name: 'radio', value: 2 }
  ]
};

/**
 * INPUT GROUP
 */
export const inputGroup = {
  legend: 'InputGroup',
  color: '#99FF00',
  isGrid: true,
  content: [
    {
      element: 'inputGroup',
      type: 'checkbox',
      appearance: 'normal',
      label: 'Instrumentos Musicais',
      name: 'intrumentos',
      value: ['Teclado', 'Gaita', 'Sax'],
      cols: 2,
      row: 0,
      col: 0,
      options: [
        { text: 'Teclado' },
        { text: 'Guitarra' },
        { text: 'Baixo' },
        { text: 'Gaita' },
        { text: 'Sax' },
        { text: 'Berimbau' }
      ]
    },
    {
      element: 'inputGroup',
      type: 'checkbox',
      appearance: 'button',
      label: 'Estilo Musical',
      name: 'estilo',
      value: ['a', 'b'],
      cols: 2,
      row: 0,
      col: 1,
      options: [
        { text: 'Rock', value: 'a' },
        { text: 'Reggae', value: 'b' },
        { text: 'Pop', value: 'c' },
        { text: 'Tango', value: 'd' },
        { text: 'Salsa', value: 'e' },
        { text: 'Samba', value: 'f' }
      ]
    },
    {
      element: 'inputGroup',
      type: 'checkbox',
      appearance: 'switch',
      label: 'Iluminação',
      name: 'estilo',
      value: ['a', 'b', 'e'],
      cols: 2,
      row: 0,
      col: 2,
      options: [
        { text: 'Verdes', value: 'a' },
        { text: 'Azuis', value: 'b' },
        { text: 'Amarelas', value: 'c' },
        { text: 'Vermelhas', value: 'd' },
        { text: 'Strobo', value: 'e' },
        { text: 'Aranhas', value: 'f' }
      ]
    },
    {
      element: 'inputGroup',
      type: 'radio',
      appearance: 'radio',
      label: 'Instrumentos Musicais',
      name: 'intrumentos',
      value: 'Guitarra',
      cols: 2,
      row: 1,
      col: 0,
      options: [
        { text: 'Teclado' },
        { text: 'Guitarra' },
        { text: 'Baixo' },
        { text: 'Gaita' },
        { text: 'Sax' },
        { text: 'Berimbau' }
      ]
    },
    {
      element: 'inputGroup',
      type: 'radio',
      appearance: 'button',
      label: 'Velocidade',
      name: 'estilo',
      value: 'b',
      cols: 1,
      row: 1,
      col: 1,
      options: [
        { text: 'Lento', value: 'a' },
        { text: 'Moderado', value: 'b' },
        { text: 'Rápido', value: 'c' }
      ]
    },
    {
      element: 'inputGroup',
      type: 'radio',
      appearance: 'switch',
      label: 'Iluminação',
      name: 'lumini',
      value: 'b',
      cols: 2,
      row: 1,
      col: 2,
      options: [
        { text: 'Verdes', value: 'a' },
        { text: 'Azuis', value: 'b' },
        { text: 'Amarelas', value: 'c' },
        { text: 'Vermelhas', value: 'd' },
        { text: 'Strobo', value: 'e' },
        { text: 'Aranhas', value: 'f' }
      ]
    }
  ]
};

/**
 * OTP E RATING
 */
export const otpRating = {
  legend: 'OTP & Rating',
  isGrid: true,
  content: [
    { element: 'otp', id: 'tutorial-otp-1', label: 'OTP 1', row: 0, col: 0, mask: '0C00', mode: 'text', value: '2F17' },
    { element: 'otp', id: 'tutorial-otp-2', label: 'OTP 2', row: 0, col: 1, mask: '0000', mode: 'password', value: '123456' },
    { element: 'rating', label: 'Rating 1', type: 'star', row: 1, col: 0 },
    { element: 'rating', label: 'Rating 2', type: 'heart', row: 1, col: 1, value: 3 }
  ]
};

/**
 * COMBO
 */
export const comboMultiple = {
  legend: 'dic.tutorial.forms.comboMultiple.title',
  isGrid: true,
  cols: 1,
  content: [
    {
      element: 'combo',
      id: 'tutorial-combo-multiple',
      name: 'tutorialTags',
      label: 'dic.tutorial.forms.comboMultiple.label',
      placeholder: 'dic.tutorial.forms.comboMultiple.placeholder',
      multiple: true,
      full: true,
      filter: true,
      sort: false,
      value: ['test', 'priority'],
      options: [
        { value: 'test', label: 'dic.tutorial.forms.comboMultiple.tags.test' },
        { value: 'priority', label: 'dic.tutorial.forms.comboMultiple.tags.priority' },
        { value: 'review', label: 'dic.tutorial.forms.comboMultiple.tags.review' },
        { value: 'done', label: 'dic.tutorial.forms.comboMultiple.tags.done' },
      ],
      row: 0,
      col: 0,
    },
  ],
};

export const tagbox = {
  legend: 'dic.tutorial.forms.tagbox.title',
  isGrid: true,
  cols: 1,
  content: [
    {
      element: 'tagbox',
      id: 'tutorial-tagbox',
      name: 'tutorialColoredTags',
      label: 'dic.tutorial.forms.tagbox.label',
      buttonText: 'dic.labels.assignTag',
      full: true,
      filter: true,
      sort: false,
      value: ['info', 'success', 'warning', 'danger', 'critical', 'error', 'muted', 'value', 'premium'],
      options: [
        { value: 'info', label: 'dic.tags.info', type: 'info' },
        { value: 'success', label: 'dic.tags.success', type: 'success' },
        { value: 'warning', label: 'dic.tags.warning', type: 'warning' },
        { value: 'danger', label: 'dic.tags.danger', type: 'danger' },
        { value: 'critical', label: 'dic.tags.critical', type: 'critical' },
        { value: 'error', label: 'dic.tags.error', type: 'error' },
        { value: 'muted', label: 'dic.tags.muted', type: 'muted' },
        { value: 'value', label: 'dic.tags.value', type: 'value' },
        { value: 'premium', label: 'dic.tags.premium', type: 'premium' },
      ],
      row: 0,
      col: 0,
    },
  ],
};

export const combo = {
  legend: 'Combo',
  isGrid: true,
  content: [
    {
      element: 'combo',
      row: 0,
      col: 0,
      size: 6,
      filter: true,
      sort: false,
      value: 'Lidiane Maekawa Pereira',
      options: [
        'Wellington Pereira',
        'Lidiane Maekawa Pereira',
        'Artur Maekawa Pereira',
        'Victor Maekawa',
        'Ziki',
        'Plic',
        'Ploc',
        'Chuvisco'
      ]
    },
    {
      element: 'combo',
      row: 0,
      col: 1,
      size: 10,
      options: [
        { icon: { type: 'ep', name: 'flags br' }, value: 'BR', text: 'Brasil' },
        { icon: { type: 'ep', name: 'flags us' }, value: 'US', text: 'Estados Unidos' },
        { icon: { type: 'ep', name: 'flags de' }, value: 'DE', text: 'Alemanha' },
        { icon: { type: 'ep', name: 'flags fr' }, value: 'FR', text: 'França' },
        { icon: { type: 'ep', name: 'flags es' }, value: 'ES', text: 'Espanha' }
      ]
    },
    {
      element: 'combo',
      row: 0,
      col: 2,
      size: 8,
      filter: true,
      value: 'jp',
      options: [
        { group: { text: 'A - Países' }, icon: { type: 'ep', name: 'flags br' }, value: 'BR', text: 'Brasil' },
        { group: { text: 'A - Países' }, icon: { type: 'ep', name: 'flags us' }, value: 'US', text: 'Estados Unidos' },
        { group: { text: 'A - Países' }, icon: { type: 'ep', name: 'flags jp' }, value: 'jp', text: 'Japão' },
        { group: { text: 'B - Estados' }, value: 'SP', text: 'São Paulo' },
        { group: { text: 'B - Estados' }, value: 'NY', text: 'Nova York' },
        { group: { text: 'B - Estados' }, value: 'HK', text: 'Hokkaido' },
        { group: { text: 'C - Cidades' }, value: 'SP', text: 'Curitiba' },
        { group: { text: 'C - Cidades' }, value: 'NY', text: 'Dallas' },
        { group: { text: 'C - Cidades' }, value: 'HK', text: 'Tokio' }
      ]
    }
  ]
};

/**
 * ADDRESS
 */
export const address = {
  legend: 'Address',
  isGrid: true,
  content: [
    { element: 'address' }
  ]
};

/**
 * TEXTAREA E EDITORES
 */
export const editors = {
  legend: 'TextArea e Editores',
  isGrid: true,
  content: [
    { element: 'textarea', label: 'TextArea', row: 0, col: 0, value: 'bom dia', maxChars: 120 },
    { element: 'editor', row: 0, col: 1, value: 'Olá mundo', maxChars: 1200 },
    { element: 'editor', row: 1, col: 0, value: 'Hello World', mode: 'full', colspan: 2 }
  ]
};

/**
 * AUDIO E VIDEO
 */
export const audioVideo = {
  legend: 'Audio e Video',
  color: '#99FF00',
  isGrid: true,
  content: [
    { element: 'audio', origin: 'ad', label: 'audio', src: '/2025-01-20/SBS.mp3', row: 0, col: 0, style: { 'margin-top': '25px' } },
    { element: 'video', origin: 'ad', label: 'video', src: '/2025-01-20/discus.mp4', row: 1, col: 0, style: { width: '615px' } }
  ]
};

/**
 * LISTAS
 */
export const lists = {
  legend: 'Lists',
  isGrid: true,
  content: [
    { element: 'list', row: 0, col: 0, list: ['Teclado', 'Guittara', 'Baixo', 'Gaita', 'Sax', 'Berimbau'] },
    { element: 'list', ordered: true, row: 0, col: 1, list: ['Rock', 'Reggae', 'Pop', 'Tango', 'Salsa', 'Samba'] },
    { element: 'list', leadingZeros: true, row: 0, col: 2, list: ['Verdes', 'Azuis', 'Amarelas', 'Vermelhas', 'Strobo', 'Aranhas'] }
  ]
};

/**
 * COMBO NO ACCORDION
 *
 * Mantido separado do combo da tab porque o tutorial antigo mostrava os dois
 * contextos de uso. Não há fábrica ou referência escondida entre os exemplos.
 */
export const comboAccordion = {
  legend: 'Combo',
  isGrid: true,
  content: [
    {
      element: 'combo',
      row: 0,
      col: 0,
      size: 6,
      sort: false,
      value: 'Lidiane Maekawa Pereira',
      options: [
        'Wellington Pereira',
        'Lidiane Maekawa Pereira',
        'Artur Maekawa Pereira',
        'Victor Maekawa',
        'Ziki',
        'Plic',
        'Ploc',
        'Chuvisco'
      ]
    },
    {
      element: 'combo',
      row: 0,
      col: 1,
      size: 10,
      options: [
        { icon: { type: 'ep', name: 'flags br' }, value: 'BR', text: 'Brasil' },
        { icon: { type: 'ep', name: 'flags us' }, value: 'US', text: 'Estados Unidos' },
        { icon: { type: 'ep', name: 'flags de' }, value: 'DE', text: 'Alemanha' },
        { icon: { type: 'ep', name: 'flags fr' }, value: 'FR', text: 'França' },
        { icon: { type: 'ep', name: 'flags es' }, value: 'ES', text: 'Espanha' }
      ]
    },
    {
      element: 'combo',
      row: 0,
      col: 2,
      size: 8,
      value: 'jp',
      options: [
        { group: { text: 'A - Países' }, icon: { type: 'ep', name: 'flags br' }, value: 'BR', text: 'Brasil' },
        { group: { text: 'A - Países' }, icon: { type: 'ep', name: 'flags us' }, value: 'US', text: 'Estados Unidos' },
        { group: { text: 'A - Países' }, icon: { type: 'ep', name: 'flags jp' }, value: 'jp', text: 'Japão' },
        { group: { text: 'B - Estados' }, value: 'SP', text: 'São Paulo' },
        { group: { text: 'B - Estados' }, value: 'NY', text: 'Nova York' },
        { group: { text: 'B - Estados' }, value: 'HK', text: 'Hokkaido' },
        { group: { text: 'C - Cidades' }, value: 'SP', text: 'Curitiba' },
        { group: { text: 'C - Cidades' }, value: 'NY', text: 'Dallas' },
        { group: { text: 'C - Cidades' }, value: 'HK', text: 'Tokio' }
      ]
    }
  ]
};

/**
 * UPLOAD
 *
 * Upload é tratado como campo de formulário neste tutorial.
 */
export const uploads = {
  legend: 'Upload',
  isGrid: true,
  content: [
    {
      element: 'upload',
      row: 0,
      col: 0,
      multiple: true,
      visibility: 'public'
    },
    {
      element: 'upload',
      row: 0,
      col: 1,
      filename: 'foto.jpg',
      accept: 'image/*',
      text: 'Subir foto',
      visibility: 'user',
      crop: {
        width: 200,
        height: 200,
        strategy: 'focal',
        variants: {
          thumb: { width: 80, height: 80 }
        }
      }
    }
  ]
};

/**
 * MULTILIST
 *
 * MultiList é campo de formulário. Não é Board.
 */
export const multiList = {
  legend: 'MultiList',
  isGrid: true,
  content: [
    {
      element: 'multiList',
      id: 'ms',
      label: 'MultiList',
      row: 0,
      col: 0,
      columns: ['personal', 'autonomous', 'mei', 'enterprise'],
      showTags: true,
      source: [
        { item_id: 'm1', item_name: 'administration', item_order: 1, item_type: 'suite' },
        { item_id: 'm2', item_name: 'users', item_order: 2, item_type: 'module' },
        { item_id: 'm3', item_name: 'list', item_order: 1, item_type: 'method' }
      ],
      target: {
        personal: {
          target_id: '1234',
          data: [
            { item_id: 'm1', item_name: 'administration', item_order: 1, item_type: 'suite' },
            { item_id: 'm2', item_name: 'users', item_order: 2, item_type: 'module' }
          ]
        },
        autonomous: {
          target_id: '5678',
          data: [
            { item_id: 'method-m3', item_name: 'list', item_order: 3, item_type: 'method' }
          ]
        }
      }
    }
  ]
};

/**
 * TREELIST
 *
 * TreeList também é campo de formulário neste tutorial.
 */
export const treeList = {
  legend: 'TreeList',
  isGrid: true,
  content: [
    {
      element: 'treeList',
      id: 'ms',
      label: 'TreeList',
      row: 0,
      col: 0,
      columns: ['personal', 'autonomous', 'mei', 'enterprise'],
      rows: 5,
      source: [
        { item_id: 'm1', item_name: 'administration', item_order: 1, item_level: 1, item_type: 'suite' },
        { item_id: 'm2', item_name: 'users', item_order: 2, item_level: 2, item_type: 'module' },
        { item_id: 'm3', item_name: 'list', item_order: 1, item_level: 3, item_type: 'method' },
        { item_id: 'm4', item_name: 'admindigital', item_order: 1, item_level: 1, item_type: 'suite' },
        { item_id: 'm7', item_name: 'clients', item_order: 3, item_level: 2, item_type: 'module' },
        { item_id: 'm6', item_name: 'types', item_order: 1, item_level: 3, item_type: 'method' }
      ],
      target: {
        personal: {
          target_id: '1234',
          data: [
            {
              item_id: 'm1',
              item_name: 'administration',
              item_order: 1,
              item_level: 1,
              item_type: 'suite',
              children: [
                {
                  item_id: 'm2',
                  item_name: 'users',
                  item_order: 2,
                  item_level: 2,
                  item_type: 'module',
                  children: [
                    {
                      item_id: 'method-m3',
                      item_name: 'list',
                      item_order: 3,
                      item_level: 3,
                      item_type: 'method'
                    }
                  ]
                }
              ]
            }
          ]
        }
      }
    }
  ]
};

/**
 * HIDDEN
 *
 * O campo hidden ficava misturado visualmente com as tags. Como é campo de
 * formulário, passa a morar aqui.
 */
export const hidden = {
  legend: 'Hidden',
  isGrid: true,
  content: [
    { element: 'hidden', name: 'ID' }
  ]
};

/**
 * AMOR
 *
 * Mantido porque já fazia parte do agrupamento original do tutorial.
 */
export const love = {
  legend: { icon: { type: 'solid', name: 'heart' }, text: 'Amor' },
  row: 0,
  col: 0,
  isGrid: true,
  content: 'Eu amo muito a peixudinha'
};

/**
 * DIÁLOGO AUXILIAR
 *
 * Este diálogo é aberto pelo botão suffix do CEP no grupo Mask.
 */
export const dialog = {
  element: 'div',
  parent: 'body',
  id: 'dialog',
  text: 'Este é um modelo de diálogo, foco na parada!',
  dialog: {
    title: 'Teste de Diálogo',
    autoOpen: false,
    buttons: [
      { text: 'Cancelar', click: () => { alert(1); } },
      { text: 'Excluir', mode: 'red', icon: { type: 'solid', name: 'trash-can', position: 'before' }, click: () => { alert(2); } },
      { icon: { type: 'solid', name: 'smile' }, mode: 'green', click: () => { alert(3); } }
    ]
  }
};
